import { useReactiveVar, WatchQueryFetchPolicy } from '@apollo/client';
import { useEffect } from 'react';
import { useActiveElementStore } from 'stores/activeElementStore';
import { useParkingStore } from 'stores/parkingStore';
import { useParkzoneStore } from 'stores/parkzoneStore';
import { ParkingZoneType } from 'types/common';
import { getRecentParkingWithSegment } from 'utils/parkUtils';
import {
  findDefaultTariff,
  isSegmentLocked,
  segmentsChanged,
} from 'utils/tariffUtils';
import useHasUnlockedCustomerSegment from './useHasUnlockedCustomerSegment';
import useParkingPricingInfo from './useParkingPricingInfo';
import useParkingZoneWithEVCheck from './useParkingZoneWithEVCheck';

/**
 * useTariffs hooks sets the preselected tariff
 * when a Park Zone Details screen is rendered.
 **/
const useTariffs = (
  fetchPolicy: WatchQueryFetchPolicy = 'cache-and-network'
) => {
  const parkingZoneUid = useActiveElementStore((state) => state.activeElement);
  const selectedTariff = useParkzoneStore((state) => state.selectedTariffId);
  const hasManuallySelectedTariff = useParkingStore(
    (state) => state.hasManuallySelectedTariff
  );
  const { pricingSegments, pricingCountryCode } = useParkingPricingInfo(
    parkingZoneUid,
    fetchPolicy === 'cache-first'
  );

  const parkingZoneResult = useParkingZoneWithEVCheck(
    parkingZoneUid,
    fetchPolicy,
    false
  );

  const { data } = parkingZoneResult;
  const { parkingMethods, commercialZoneView } = data?.parkZone || {};

  const defaultSegment = findDefaultTariff(pricingSegments);
  const previousUsedTariff = getRecentParkingWithSegment(
    commercialZoneView?.zoneCode || ''
  );
  const { isUnlockValid } = useHasUnlockedCustomerSegment(
    previousUsedTariff?.customerSegmentUid || defaultSegment?.uid || '',
    pricingCountryCode || 'SE',
    commercialZoneView?.zoneCode || ''
  );

  const setTariff = (
    type: ParkingZoneType,
    name?: string | undefined | null,
    id?: string | undefined | null,
    secondaryId?: string,
    setTariffIdName = true
  ) => {
    if (setTariffIdName) {
      useParkzoneStore.getState().setSelectedTariffName(name || undefined);
      useParkzoneStore.getState().setSelectedTariffId(id || undefined);
    }
    useActiveElementStore
      .getState()
      .setActiveElementTypeAndStation(type, secondaryId ?? '');
  };

  useEffect(() => {
    /**
     * Set default tariff only when there is not selected tariff or there has been changes in segments
     */
    if (
      selectedTariff === undefined ||
      segmentsChanged(
        selectedTariff,
        pricingSegments,
        hasManuallySelectedTariff
      )
    ) {
      if (pricingSegments?.length > 0) {
        /**
         * A managed Park Zone can be used to start a parking session,
         * therefore default tariff should be the first price scheme
         **/

        // SE zones might have default segment locked also, so we need to check that and also if customers unlocking is valid
        const isDefaultLocked = isSegmentLocked(defaultSegment, isUnlockValid);
        const parkAndCharge =
          !parkingMethods?.unmanagedParking && parkingMethods?.evCharging;

        if (!isDefaultLocked) {
          const privateTariff = pricingSegments.find(
            (pricingSegment) => pricingSegment?.accessType === 'PRIVATE'
          );
          const privateOrDefault = privateTariff || defaultSegment;
          // Check if previous used tariff is within the allowed tariffs of the selected park zone.
          const isPreviousUsedTariffAllowed =
            previousUsedTariff &&
            pricingSegments.find(
              (pricingSegment) =>
                pricingSegment?.uid === previousUsedTariff.customerSegmentUid &&
                (pricingSegment?.accessType !== 'LOCKED' || isUnlockValid)
            );
          setTariff(
            parkAndCharge ? 'PARK_AND_CHARGE' : 'PARKING',
            isPreviousUsedTariffAllowed
              ? previousUsedTariff.customerSegmentName
              : privateOrDefault?.title,
            isPreviousUsedTariffAllowed
              ? previousUsedTariff.customerSegmentUid
              : privateOrDefault?.uid
          );
        }
      }
    }
  }, [
    defaultSegment,
    isUnlockValid,
    pricingSegments,
    parkingMethods,
    commercialZoneView,
    hasManuallySelectedTariff,
  ]);

  return {};
};

export default useTariffs;
